/**
 * MigrateListingsPage — Copy Listings to xrp.deals
 *
 * Shows NFTs the user has listed on other marketplaces and allows them
 * to create matching sell offers brokered by xrp.deals (lower 1% fee).
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router';
import {
  FeatherArrowRight,
  FeatherCheck,
  FeatherX,
  FeatherAlertCircle,
  FeatherClock,
} from '@subframe/core';
import { DefaultPageLayout } from '../ui';
import { Button } from '../ui/components/Button';
import { Dialog } from '../ui/components/Dialog';
import { Progress } from '../ui/components/Progress';
import { useAuthStore } from '../store/authStore';
import { useNotificationStore } from '../store/notificationStore';
import { apiClient } from '../api/client';
import { DEALS_BROKER_ADDRESS, DEALS_FEE_PERCENTAGE } from '../lib/marketplace-constants';
import { is3DModelUrl } from '../utils/media';
import { WalletSigningModal } from '../components/transactions/WalletSigningModal';
import { toSigningPayload, getWalletProvider } from '../lib/wallet';
import type { SigningPayload } from '../types/signing';
import { useXamanPayloadStatus } from '../hooks/useXamanPayloadStatus';
import { useWcSigningRequest } from '../hooks/useWcSigningRequest';
import {
  useMigrateCheckout,
  type EligibleNft,
  type MigrateItemState,
  type MigrateItemStatus,
} from '../hooks/useMigrateCheckout';

export function MigrateListingsPage() {
  const { user, isAuthenticated, updateUser } = useAuthStore();
  const { success: showSuccess, error: showError } = useNotificationStore();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [eligibleNfts, setEligibleNfts] = useState<EligibleNft[]>([]);

  // Single offer signing state
  const [signingNft, setSigningNft] = useState<EligibleNft | null>(null);
  const [signingPayload, setSigningPayload] = useState<SigningPayload | null>(null);
  const [signingLoading, setSigningLoading] = useState<string | null>(null); // nft_token_id being signed

  // Bulk checkout
  const migrateCheckout = useMigrateCheckout();
  const [checkoutModalOpen, setCheckoutModalOpen] = useState(false);

  const fetchEligibleNfts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get<any>('/migrate/eligible-nfts');
      if (response.success) {
        setEligibleNfts(response.data.nfts);
        // If no eligible NFTs, update user store to hide banner
        if (response.data.nfts.length === 0) {
          updateUser({ show_migrate_banner: false });
        }
      } else {
        setError(response.error || 'Failed to load eligible NFTs');
      }
    } catch (err) {
      setError('Failed to load eligible NFTs');
    } finally {
      setLoading(false);
    }
  }, [updateUser]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchEligibleNfts();
    }
  }, [isAuthenticated, fetchEligibleNfts]);

  // Handle single offer copy
  const handleSingleCopy = async (nft: EligibleNft) => {
    if (!user?.xrpl_address) return;
    setSigningLoading(nft.nft_token_id);

    try {
      const response = await apiClient.post<any>('/offers/create', {
        nftTokenId: nft.nft_token_id,
        offerType: 'sell',
        amount: nft.offer.price_drops,
        userAddress: nft.owner_address,
        destination: DEALS_BROKER_ADDRESS,
      });

      if (response.success && (response.data?.signingIntent || response.data?.payload)) {
        setSigningPayload(toSigningPayload(response.data.signingIntent ?? response.data.payload));
        setSigningNft(nft);
      } else {
        showError(response.error || 'Failed to create listing');
      }
    } catch {
      showError('Failed to create listing');
    } finally {
      setSigningLoading(null);
    }
  };

  const handleSigningSuccess = () => {
    if (signingNft) {
      showSuccess(`Listed ${signingNft.name} on xrp.deals!`);
      setEligibleNfts((prev) => prev.filter((n) => n.nft_token_id !== signingNft.nft_token_id));
    }
    setSigningNft(null);
    setSigningPayload(null);
  };

  const handleSigningClose = () => {
    setSigningNft(null);
    setSigningPayload(null);
  };

  // Handle bulk copy (max 10 at a time)
  const BATCH_SIZE = 10;
  const handleBulkCopy = () => {
    const batch = eligibleNfts.slice(0, BATCH_SIZE);
    migrateCheckout.startConfirmation(batch);
    setCheckoutModalOpen(true);
  };

  const handleCheckoutClose = () => {
    if (migrateCheckout.state.stage !== 'creating') {
      migrateCheckout.reset();
      setCheckoutModalOpen(false);
    }
  };

  const handleCheckoutComplete = () => {
    // Remove signed NFTs from the table
    const signedTokenIds = new Set(
      migrateCheckout.state.items
        .filter((i) => i.status === 'signed')
        .map((i) => i.nft.nft_token_id)
    );
    setEligibleNfts((prev) => prev.filter((n) => !signedTokenIds.has(n.nft_token_id)));

    migrateCheckout.reset();
    setCheckoutModalOpen(false);

    if (signedTokenIds.size > 0) {
      showSuccess(`Listed ${signedTokenIds.size} NFT${signedTokenIds.size !== 1 ? 's' : ''} on xrp.deals!`);
    }
  };

  // Redirect if not authenticated
  if (!isAuthenticated) {
    return (
      <DefaultPageLayout>
        <div className="flex flex-col items-center justify-center gap-4 py-16">
          <p className="text-heading-3 font-heading-3 text-default-font">Sign in to continue</p>
          <p className="text-body font-body text-subtext-color">
            You need to sign in to copy your listings to xrp.deals.
          </p>
        </div>
      </DefaultPageLayout>
    );
  }

  return (
    <DefaultPageLayout>
      <div className="flex flex-col gap-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mobile:flex-col">
          <div className="flex flex-col gap-2">
            <h1 className="text-heading-2 font-heading-2 text-default-font">
              Copy Listings to xrp.deals
            </h1>
            <p className="text-body font-body text-subtext-color max-w-2xl">
              You have NFTs listed on other marketplaces with higher fees. Copy them to xrp.deals at the same prices
              with just a {DEALS_FEE_PERCENTAGE}% fee — save money on every sale.
            </p>
          </div>
          {eligibleNfts.length > 1 && (
            <Button
              variant="brand-primary"
              onClick={handleBulkCopy}
              className="flex-shrink-0"
            >
              {eligibleNfts.length > BATCH_SIZE
                ? `Sign Next ${BATCH_SIZE}`
                : `List All on xrp.deals (${eligibleNfts.length})`}
            </Button>
          )}
        </div>

        {/* Loading state */}
        {loading && (
          <div className="flex items-center justify-center py-16">
            <div className="w-6 h-6 border-2 border-brand-600 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="p-4 bg-error-50 rounded-md border border-solid border-error-200">
            <p className="text-body font-body text-error-700">{error}</p>
            <Button variant="neutral-secondary" size="small" onClick={fetchEligibleNfts} className="mt-2">
              Retry
            </Button>
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && eligibleNfts.length === 0 && (
          <div className="flex flex-col items-center justify-center gap-4 py-16">
            <div className="w-16 h-16 rounded-full bg-success-50 flex items-center justify-center">
              <FeatherCheck className="w-8 h-8 text-success-600" />
            </div>
            <p className="text-heading-3 font-heading-3 text-default-font">
              All caught up!
            </p>
            <p className="text-body font-body text-subtext-color text-center max-w-md">
              All your listed NFTs are already on xrp.deals, or you don't have any listings on other marketplaces.
            </p>
            <Link to="/">
              <Button variant="brand-primary">Back to Home</Button>
            </Link>
          </div>
        )}

        {/* NFT Table */}
        {!loading && !error && eligibleNfts.length > 0 && (
          <div className="border border-solid border-neutral-border rounded-md overflow-hidden">
            {/* Table header */}
            <div className="mobile:hidden grid grid-cols-[1fr_1fr_120px_120px_140px] gap-3 px-4 py-3 bg-neutral-50 border-b border-solid border-neutral-border">
              <span className="text-caption-bold font-caption-bold text-subtext-color">NFT</span>
              <span className="text-caption-bold font-caption-bold text-subtext-color">Collection</span>
              <span className="text-caption-bold font-caption-bold text-subtext-color">Price</span>
              <span className="text-caption-bold font-caption-bold text-subtext-color">Listed On</span>
              <span className="text-caption-bold font-caption-bold text-subtext-color text-right">Action</span>
            </div>

            {/* Table rows */}
            {eligibleNfts.map((nft, index) => (
              <div
                key={nft.nft_token_id}
                className={`grid grid-cols-[1fr_1fr_120px_120px_140px] gap-3 px-4 py-3 items-center mobile:flex mobile:flex-col mobile:items-start mobile:gap-2 ${
                  index !== eligibleNfts.length - 1 ? 'border-b border-solid border-neutral-border' : ''
                }`}
              >
                {/* NFT */}
                <Link to={`/nfts/${nft.nft_token_id}`} className="flex items-center gap-3 min-w-0">
                  {is3DModelUrl(nft.thumbnail_url) ? (
                    <div className="w-12 h-12 rounded-md flex items-center justify-center bg-neutral-100 flex-shrink-0">
                      <span style={{ fontSize: '20px' }}>🧊</span>
                    </div>
                  ) : (
                    <img
                      src={nft.thumbnail_url || '/placeholder-nft.png'}
                      alt={nft.name}
                      className="w-12 h-12 rounded-md object-cover flex-shrink-0"
                    />
                  )}
                  <span className="text-body-bold font-body-bold text-default-font truncate hover:text-brand-600 transition-colors">
                    {nft.name}
                  </span>
                </Link>

                {/* Collection */}
                <div className="min-w-0">
                  {nft.collection_slug ? (
                    <Link
                      to={`/collections/${nft.collection_slug}`}
                      className="text-body font-body text-brand-600 hover:underline truncate block"
                    >
                      {nft.collection_name || 'Unknown'}
                    </Link>
                  ) : (
                    <span className="text-body font-body text-subtext-color truncate block">
                      {nft.collection_name || 'Unknown'}
                    </span>
                  )}
                </div>

                {/* Price */}
                <span className="text-body-bold font-body-bold text-default-font">
                  {nft.offer.price.toFixed(2)} XRP
                </span>

                {/* Marketplace badge */}
                <span
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold w-fit"
                  style={{
                    background: 'linear-gradient(135deg, #6d4c41 0%, #8d6e63 100%)',
                    color: 'white',
                  }}
                >
                  {nft.offer.marketplace_name}
                </span>

                {/* Action */}
                <div className="flex justify-end mobile:w-full">
                  <Button
                    variant="brand-primary"
                    size="small"
                    onClick={() => handleSingleCopy(nft)}
                    loading={signingLoading === nft.nft_token_id}
                    disabled={!!signingLoading}
                    className="mobile:w-full"
                  >
                    List on xrp.deals
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Single offer signing modal */}
      {signingPayload && signingNft && (
        <WalletSigningModal
          open={true}
          onClose={handleSigningClose}
          title="List on xrp.deals"
          payload={signingPayload}
          onSuccess={handleSigningSuccess}
          onRejected={() => {
            showError('Transaction was rejected');
            handleSigningClose();
          }}
          onExpired={() => {
            showError('Transaction expired');
            handleSigningClose();
          }}
        />
      )}

      {/* Bulk checkout modal */}
      <MigrateCheckoutModal
        open={checkoutModalOpen}
        onClose={handleCheckoutClose}
        nfts={migrateCheckout.nftsToMigrate}
        totalEligible={eligibleNfts.length}
        state={migrateCheckout.state}
        onStartCreating={migrateCheckout.startCreating}
        onCancelConfirmation={() => {
          migrateCheckout.reset();
          setCheckoutModalOpen(false);
        }}
        onResolveCurrentItem={migrateCheckout.resolveCurrentItem}
        onCancelRemaining={migrateCheckout.cancelRemaining}
        onComplete={handleCheckoutComplete}
      />
    </DefaultPageLayout>
  );
}

// ============================================================================
// MigrateCheckoutModal — mirrors CartCheckoutModal
// ============================================================================

interface MigrateCheckoutModalProps {
  open: boolean;
  onClose: () => void;
  nfts: EligibleNft[];
  totalEligible: number;
  state: ReturnType<typeof useMigrateCheckout>['state'];
  onStartCreating: () => void;
  onCancelConfirmation: () => void;
  onResolveCurrentItem: (status: 'signed' | 'rejected' | 'expired', error?: string) => void;
  onCancelRemaining: () => void;
  onComplete: () => void;
}

function MigrateCheckoutModal({
  open,
  onClose,
  nfts,
  totalEligible,
  state,
  onStartCreating,
  onCancelConfirmation,
  onResolveCurrentItem,
  onCancelRemaining,
  onComplete,
}: MigrateCheckoutModalProps) {
  const { stage, items, completedCount, failedCount } = state;

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <Dialog.Content className="max-w-lg w-full p-0 mobile:max-w-full mobile:m-4 !gap-0 !items-stretch">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-solid border-neutral-border">
          <div className="flex items-center gap-2">
            <FeatherArrowRight className="h-5 w-5 text-brand-600" />
            <h2 className="text-heading-3 font-heading-3 text-default-font">
              {stage === 'confirming' && 'Confirm Bulk Listing'}
              {stage === 'creating' && 'Creating Listings'}
              {stage === 'complete' && 'Listing Complete'}
            </h2>
          </div>
          {stage !== 'creating' && (
            <button
              onClick={onClose}
              className="p-1 rounded hover:bg-neutral-100 transition-colors"
            >
              <FeatherX className="h-5 w-5 text-subtext-color" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="p-4 mobile:p-3">
          {stage === 'confirming' && (
            <ConfirmContent
              nfts={nfts}
              isPartialBatch={nfts.length < totalEligible}
              onStart={onStartCreating}
              onCancel={onCancelConfirmation}
            />
          )}

          {stage === 'creating' && (
            <CreatingContent
              items={items}
              currentPayload={state.currentPayload}
              currentIndex={state.currentIndex}
              onResolveCurrentItem={onResolveCurrentItem}
              onCancelRemaining={onCancelRemaining}
            />
          )}

          {stage === 'complete' && (
            <CompleteContent
              completedCount={completedCount}
              failedCount={failedCount}
              items={items}
              onComplete={onComplete}
            />
          )}
        </div>
      </Dialog.Content>
    </Dialog>
  );
}

// ============================================================================
// Confirmation Content
// ============================================================================

function ConfirmContent({
  nfts,
  isPartialBatch,
  onStart,
  onCancel,
}: {
  nfts: EligibleNft[];
  isPartialBatch: boolean;
  onStart: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="flex flex-col gap-4">
      <div className="p-3 bg-brand-50 rounded-md">
        <p className="text-body font-body text-brand-700">
          You will sign {nfts.length} transaction{nfts.length !== 1 ? 's' : ''} in Xaman to create sell offers
          on xrp.deals at the same prices as your existing listings.
        </p>
      </div>

      <div className="max-h-[250px] overflow-y-auto border border-solid border-neutral-border rounded-md mobile:max-h-[200px]">
        {nfts.map((nft, index) => (
          <div
            key={nft.nft_token_id}
            className={`flex items-center gap-3 p-3 ${index !== nfts.length - 1 ? 'border-b border-solid border-neutral-border' : ''}`}
          >
            {is3DModelUrl(nft.thumbnail_url) ? (
              <div className="w-12 h-12 rounded-md flex items-center justify-center bg-neutral-100 flex-shrink-0">
                <span style={{ fontSize: '20px' }}>🧊</span>
              </div>
            ) : (
              <img
                src={nft.thumbnail_url || '/placeholder-nft.png'}
                alt={nft.name}
                className="w-12 h-12 rounded-md object-cover"
              />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-body-bold font-body-bold text-default-font truncate">{nft.name}</p>
              {nft.collection_name && (
                <p className="text-caption font-caption text-subtext-color truncate">{nft.collection_name}</p>
              )}
            </div>
            <span className="text-body-bold font-body-bold text-default-font whitespace-nowrap">
              {nft.offer.price.toFixed(2)} XRP
            </span>
          </div>
        ))}
      </div>

      <div className="flex gap-2 mobile:flex-col-reverse">
        <Button variant="neutral-secondary" onClick={onCancel} className="flex-1 mobile:w-full">
          Cancel
        </Button>
        <Button variant="brand-primary" onClick={onStart} className="flex-1 mobile:w-full">
          {isPartialBatch
            ? `Sign ${nfts.length} Listings`
            : 'List All on xrp.deals'}
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// Creating Content — live status
// ============================================================================

function CreatingContent({
  items,
  currentPayload,
  currentIndex,
  onResolveCurrentItem,
  onCancelRemaining,
}: {
  items: MigrateItemState[];
  currentPayload: SigningPayload | null;
  currentIndex: number;
  onResolveCurrentItem: (status: 'signed' | 'rejected' | 'expired', error?: string) => void;
  onCancelRemaining: () => void;
}) {
  const totalCount = items.length;
  const resolvedCount = items.filter(
    (i) => i.status === 'signed' || i.status === 'rejected' || i.status === 'expired' || i.status === 'error'
  ).length;
  const signedCount = items.filter((i) => i.status === 'signed').length;
  const remainingCount = totalCount - resolvedCount;
  const progressPercent = (resolvedCount / totalCount) * 100;
  const currentItem = items[currentIndex];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <div className="flex justify-between items-center">
          <span className="text-body-bold font-body-bold text-default-font">
            {signedCount} of {totalCount} listed
          </span>
          <span className="text-caption font-caption text-subtext-color">
            {currentIndex + 1} of {totalCount}
          </span>
        </div>
        <Progress value={progressPercent} />
      </div>

      {/* Current item QR code + signing area */}
      {currentItem && (
        <div className="flex flex-col items-center gap-3 p-4 border border-solid border-brand-200 rounded-md bg-brand-50">
          <div className="flex items-center gap-3 w-full">
            {is3DModelUrl(currentItem.nft.thumbnail_url) ? (
              <div className="w-12 h-12 rounded-md flex items-center justify-center bg-neutral-100 flex-shrink-0">
                <span style={{ fontSize: '20px' }}>🧊</span>
              </div>
            ) : (
              <img
                src={currentItem.nft.thumbnail_url || '/placeholder-nft.png'}
                alt={currentItem.nft.name}
                className="w-12 h-12 rounded-md object-cover flex-shrink-0"
              />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-body-bold font-body-bold text-default-font truncate">
                {currentItem.nft.name}
              </p>
              <p className="text-caption font-caption text-subtext-color">
                {currentItem.nft.offer.price.toFixed(2)} XRP
              </p>
            </div>
          </div>

          {currentItem.status === 'creating' && (
            <div className="flex items-center gap-2 py-4">
              <div className="w-5 h-5 border-2 border-brand-600 border-t-transparent rounded-full animate-spin" />
              <span className="text-body font-body text-brand-700">Creating payload...</span>
            </div>
          )}

          {currentPayload && (
            <ActivePayloadMonitor
              payload={currentPayload}
              onSigned={() => onResolveCurrentItem('signed')}
              onRejected={() => onResolveCurrentItem('rejected')}
              onExpired={() => onResolveCurrentItem('expired')}
            />
          )}
        </div>
      )}

      {/* Item list */}
      <div className="max-h-[180px] overflow-y-auto border border-solid border-neutral-border rounded-md">
        {items.map((ci, index) => (
          <div
            key={ci.nft.nft_token_id}
            className={`flex items-center gap-3 p-2 ${index !== items.length - 1 ? 'border-b border-solid border-neutral-border' : ''} ${index === currentIndex ? 'bg-brand-50' : ''}`}
          >
            <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
              <StatusIcon status={ci.status} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-caption-bold font-caption-bold text-default-font truncate">{ci.nft.name}</p>
            </div>
            <span className="text-caption font-caption text-default-font whitespace-nowrap flex-shrink-0">
              {ci.nft.offer.price.toFixed(2)} XRP
            </span>
          </div>
        ))}
      </div>

      {remainingCount > 0 && (
        <Button variant="neutral-secondary" onClick={onCancelRemaining} className="w-full">
          Cancel Remaining ({remainingCount} left)
        </Button>
      )}
    </div>
  );
}

// ============================================================================
// ActivePayloadMonitor — uses useXamanPayloadStatus to show QR + track status
// ============================================================================

interface ActivePayloadMonitorProps {
  payload: SigningPayload;
  onSigned: () => void;
  onRejected: () => void;
  onExpired: () => void;
}

// Provider-aware: Xaman renders QR + WS/polling; client-signing (joey-wc) signs over
// the WC session and verifies on-chain, showing an approve-in-wallet prompt instead.
function ActivePayloadMonitor(props: ActivePayloadMonitorProps) {
  return getWalletProvider(props.payload.provider).capabilities.clientSigning
    ? <WcPayloadMonitor {...props} />
    : <XamanPayloadMonitor {...props} />;
}

function XamanPayloadMonitor({ payload, onSigned, onRejected, onExpired }: ActivePayloadMonitorProps) {
  const { status } = useXamanPayloadStatus({
    uuid: payload.id,
    websocketUrl: payload.websocketUrl,
    onSigned: () => onSigned(),
    onRejected: () => onRejected(),
    onExpired: () => onExpired(),
  });

  const handleDeepLinkClick = () => {
    if (payload.deepLink) window.open(payload.deepLink, '_blank');
  };

  const showQR = (status === 'pending' || status === 'opened') && !!payload.qrUrl;
  const showStatus = status === 'signing' || status === 'dispatching';

  return (
    <div className="flex flex-col items-center gap-3 w-full">
      {showQR && (
        <>
          <p className="text-caption font-caption text-brand-700 text-center">
            Scan with Xaman or tap below to sign
          </p>
          <img src={payload.qrUrl} alt="Xaman QR Code" style={{ width: 180, height: 180 }} />
          <Button variant="neutral-secondary" size="medium" onClick={handleDeepLinkClick} className="w-full">
            Open in Xaman
          </Button>
        </>
      )}

      {showStatus && (
        <div className="flex items-center gap-2 py-4">
          <div className="w-5 h-5 border-2 border-brand-600 border-t-transparent rounded-full animate-spin" />
          <span className="text-body font-body text-brand-700">
            {status === 'signing' ? 'Signing...' : 'Processing on XRPL...'}
          </span>
        </div>
      )}
    </div>
  );
}

function WcPayloadMonitor({ payload, onSigned, onRejected, onExpired }: ActivePayloadMonitorProps) {
  const { status } = useWcSigningRequest({
    payload,
    onSigned: () => onSigned(),
    onRejected: () => onRejected(),
    onExpired: () => onExpired(),
  });

  return (
    <div className="flex flex-col items-center gap-2 py-4">
      <div className="w-5 h-5 border-2 border-brand-600 border-t-transparent rounded-full animate-spin" />
      <span className="text-body font-body text-brand-700">
        {status === 'dispatching' ? 'Confirming on XRPL...' : 'Approve in your wallet...'}
      </span>
    </div>
  );
}

function StatusIcon({ status }: { status: MigrateItemStatus }) {
  switch (status) {
    case 'creating':
      return <div className="w-5 h-5 border-2 border-brand-600 border-t-transparent rounded-full animate-spin" />;
    case 'pending':
      return <FeatherClock className="w-5 h-5 text-brand-600" />;
    case 'signed':
      return <FeatherCheck className="w-5 h-5 text-success-600" />;
    case 'rejected':
    case 'expired':
    case 'error':
      return <FeatherX className="w-5 h-5 text-error-600" />;
    default:
      return <FeatherClock className="w-5 h-5 text-subtext-color" />;
  }
}

function StatusLabel({ status, error }: { status: MigrateItemStatus; error?: string }) {
  switch (status) {
    case 'waiting':
      return <>Queued</>;
    case 'creating':
      return <>Sending to Xaman...</>;
    case 'pending':
      return <>Waiting for signature</>;
    case 'signed':
      return <span className="text-success-600">Listed on xrp.deals</span>;
    case 'rejected':
      return <span className="text-error-600">{error || 'Rejected'}</span>;
    case 'expired':
      return <span className="text-error-600">Expired</span>;
    case 'error':
      return <span className="text-error-600">{error || 'Failed'}</span>;
    default:
      return <>Pending</>;
  }
}

// ============================================================================
// Complete Content
// ============================================================================

function CompleteContent({
  completedCount,
  failedCount,
  items,
  onComplete,
}: {
  completedCount: number;
  failedCount: number;
  items: MigrateItemState[];
  onComplete: () => void;
}) {
  const hasSuccesses = completedCount > 0;
  const hasFailures = failedCount > 0;
  const failedItems = items.filter(
    (i) => i.status === 'rejected' || i.status === 'expired' || i.status === 'error'
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col items-center gap-3 py-4">
        {hasSuccesses && !hasFailures && (
          <>
            <div className="w-16 h-16 rounded-full bg-success-50 flex items-center justify-center">
              <FeatherCheck className="w-8 h-8 text-success-600" />
            </div>
            <p className="text-heading-3 font-heading-3 text-default-font">
              All listings created!
            </p>
            <p className="text-body font-body text-subtext-color">
              Successfully listed {completedCount} NFT{completedCount !== 1 ? 's' : ''} on xrp.deals
            </p>
          </>
        )}

        {hasSuccesses && hasFailures && (
          <>
            <div className="w-16 h-16 rounded-full bg-warning-50 flex items-center justify-center">
              <FeatherAlertCircle className="w-8 h-8 text-warning-600" />
            </div>
            <p className="text-heading-3 font-heading-3 text-default-font">
              Partially completed
            </p>
            <p className="text-body font-body text-subtext-color">
              {completedCount} listed, {failedCount} failed
            </p>
          </>
        )}

        {!hasSuccesses && hasFailures && (
          <>
            <div className="w-16 h-16 rounded-full bg-error-50 flex items-center justify-center">
              <FeatherX className="w-8 h-8 text-error-600" />
            </div>
            <p className="text-heading-3 font-heading-3 text-default-font">
              Listing failed
            </p>
            <p className="text-body font-body text-subtext-color">
              No NFTs were listed
            </p>
          </>
        )}

        {!hasSuccesses && !hasFailures && (
          <>
            <div className="w-16 h-16 rounded-full bg-neutral-100 flex items-center justify-center">
              <FeatherX className="w-8 h-8 text-subtext-color" />
            </div>
            <p className="text-heading-3 font-heading-3 text-default-font">
              Listing cancelled
            </p>
          </>
        )}
      </div>

      {hasFailures && (
        <div className="flex flex-col gap-2">
          <p className="text-body-bold font-body-bold text-error-600">
            Failed listings:
          </p>
          <div className="max-h-[150px] overflow-y-auto border border-solid border-error-200 rounded-md bg-error-50">
            {failedItems.map((item, index) => (
              <div
                key={item.nft.nft_token_id}
                className={`p-2 ${index !== failedItems.length - 1 ? 'border-b border-solid border-error-200' : ''}`}
              >
                <p className="text-body-bold font-body-bold text-error-700 truncate">
                  {item.nft.name}
                </p>
                <p className="text-caption font-caption text-error-600">
                  {item.error || item.status}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      <Button variant="brand-primary" onClick={onComplete} className="w-full">
        Done
      </Button>
    </div>
  );
}
